import { Alert, Box, CircularProgress, Stack, Typography } from '@mui/material'
import { useContext, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { com } from '@eidu/form'
import HierarchyPage from './HierarchyPage'
import useEntityById from '../../io/useEntityById'
import { EntityTypesContext } from '../../io/context/EntityTypes'
import { buildHierarchyFromEntity } from './hierarchyAccess'

import EntityId = com.eidu.sharedlib.entity.EntityId
import entityIdFromString = com.eidu.sharedlib.entity.entityIdFromString

const HierarchyRoute = () => {
  const { hierarchyId: hierarchyIdParam } = useParams<{ hierarchyId: string }>()
  const entityTypesRepository = useContext(EntityTypesContext)

  const entityTypes = entityTypesRepository.useAll()
  const entityTypesError = entityTypesRepository.useError()
  const entityTypesLoading = entityTypesRepository.useLoading()

  const parsedHierarchyId = useMemo<EntityId | undefined | null>(() => {
    if (!hierarchyIdParam) return undefined
    try {
      return entityIdFromString(hierarchyIdParam)
    } catch {
      return null
    }
  }, [hierarchyIdParam])

  const { entity: hierarchyEntity, loading: hierarchyLoading } = useEntityById(parsedHierarchyId ?? undefined)
  const matchesRequestedHierarchy = useMemo(() => {
    if (!parsedHierarchyId || !hierarchyEntity) return false
    return hierarchyEntity.entity.id.equals(parsedHierarchyId)
  }, [hierarchyEntity, parsedHierarchyId])

  const currentHierarchyEntity = matchesRequestedHierarchy ? hierarchyEntity : undefined

  const buildResult = useMemo(() => {
    if (!entityTypes || !currentHierarchyEntity) return { name: '', levels: [], errors: [] }
    return buildHierarchyFromEntity(currentHierarchyEntity, entityTypes)
  }, [currentHierarchyEntity, entityTypes])

  if (!hierarchyIdParam) {
    return (
      <Stack padding={3} spacing={2}>
        <Alert severity="info">No hierarchy selected.</Alert>
      </Stack>
    )
  }

  if (parsedHierarchyId === null) {
    return (
      <Stack padding={3} spacing={2}>
        <Alert severity="error">The hierarchy ID is invalid.</Alert>
      </Stack>
    )
  }

  if (entityTypesError) {
    return (
      <Stack padding={3} spacing={2}>
        <Alert severity="error">{String(entityTypesError)}</Alert>
      </Stack>
    )
  }

  const staleHierarchyEntityLoaded =
    hierarchyEntity !== undefined && hierarchyEntity !== null && !matchesRequestedHierarchy

  if (
    entityTypesLoading ||
    hierarchyLoading ||
    !entityTypes ||
    hierarchyEntity === undefined ||
    staleHierarchyEntityLoaded
  ) {
    return (
      <Stack padding={3} spacing={2} sx={{ position: 'relative', height: '100%', minHeight: 0 }}>
        <Box sx={{ py: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">
              Loading...
            </Typography>
          </Stack>
        </Box>
      </Stack>
    )
  }

  if (hierarchyEntity === null) {
    return (
      <Stack padding={3} spacing={2}>
        <Alert severity="error">Hierarchy not found.</Alert>
      </Stack>
    )
  }

  const { name, levels, errors } = buildResult

  if (errors.length > 0 || levels.length === 0) {
    return (
      <Stack padding={3} spacing={2}>
        <Alert severity="error">
          <Stack spacing={0.5}>
            <Typography fontWeight={600}>Could not render the hierarchy.</Typography>
            {errors.map((message) => (
              <Typography key={message} variant="body2">
                {message}
              </Typography>
            ))}
          </Stack>
        </Alert>
      </Stack>
    )
  }

  return (
    <Stack padding={3} spacing={2} sx={{ height: '100%', minHeight: 0 }}>
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <HierarchyPage
          key={hierarchyIdParam}
          hierarchyId={hierarchyIdParam}
          title={name}
          levels={levels}
          entityTypes={entityTypes}
        />
      </Box>
    </Stack>
  )
}

export default HierarchyRoute
